import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PhoneInput, isValidE164 } from "@/components/PhoneInput";
import { Check, Loader2, Coins, Sparkles, Phone } from "lucide-react";
import { toast } from "sonner";

function PlanApplicationDialog({ plan, onClose, trialMode }) {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({
    full_name: user?.name || "", email: user?.email || "", phone: user?.phone || "",
    country: "", company: "", notes: "", est_usage: "", use_case: "", company_size: "",
  });
  const [busy, setBusy] = useState(false);
  const isUltimate = plan?.id === "ultimate_api" || plan?.contact_sales;
  const planId = plan?.contact_sales ? "ultimate_api" : plan?.id;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));

  const submit = async () => {
    if (!form.full_name.trim()) return toast.error("Full name is required");
    if (!isValidE164(form.phone)) return toast.error("A valid international phone number is required");
    if (!form.country.trim()) return toast.error("Country is required");
    setBusy(true);
    try {
      const { data } = await api.post("/billing/plan-applications", { ...form, plan: planId });
      refreshUser();
      if (data.checkout_available) {
        const co = await api.post("/payments/checkout", { plan: planId, application_id: data.application_id });
        window.location.href = co.data.collect_url;
        return;
      }
      toast.success(trialMode ? "Application received — we'll contact you for early access." : "Application received — our team will contact you shortly.");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit application");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!plan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#101219] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto" data-testid="plan-application-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Apply for {plan?.name}</DialogTitle>
          <DialogDescription className="text-white/45 text-xs">
            {trialMode ? "AlphaCore is in trial mode — apply and we'll onboard you." : "Complete your details to continue."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5 mt-1">
          <div>
            <div className="kr-overline mb-1.5">FULL NAME *</div>
            <Input value={form.full_name} onChange={set("full_name")} className="bg-[#07080B] border-white/10 h-11" data-testid="app-fullname" />
          </div>
          <div>
            <div className="kr-overline mb-1.5">EMAIL *</div>
            <Input value={form.email} onChange={set("email")} type="email" className="bg-[#07080B] border-white/10 h-11" data-testid="app-email" />
          </div>
          <div>
            <div className="kr-overline mb-1.5">PHONE NUMBER *</div>
            <PhoneInput value={form.phone} onChange={(full) => setForm((f) => ({ ...f, phone: full }))} testid="app-phone" />
          </div>
          <div>
            <div className="kr-overline mb-1.5">COUNTRY *</div>
            <Input value={form.country} onChange={set("country")} placeholder="e.g. Lebanon" className="bg-[#07080B] border-white/10 h-11" data-testid="app-country" />
          </div>
          <div>
            <div className="kr-overline mb-1.5">COMPANY (OPTIONAL)</div>
            <Input value={form.company} onChange={set("company")} className="bg-[#07080B] border-white/10 h-11" data-testid="app-company" />
          </div>
          {isUltimate && (
            <>
              <div>
                <div className="kr-overline mb-1.5">ESTIMATED MONTHLY USAGE</div>
                <Input value={form.est_usage} onChange={set("est_usage")} placeholder="e.g. 50k generations" className="bg-[#07080B] border-white/10 h-11" data-testid="app-usage" />
              </div>
              <div>
                <div className="kr-overline mb-1.5">API USE CASE</div>
                <Input value={form.use_case} onChange={set("use_case")} className="bg-[#07080B] border-white/10 h-11" data-testid="app-usecase" />
              </div>
              <div>
                <div className="kr-overline mb-1.5">COMPANY SIZE</div>
                <Input value={form.company_size} onChange={set("company_size")} placeholder="e.g. 10-50" className="bg-[#07080B] border-white/10 h-11" data-testid="app-companysize" />
              </div>
            </>
          )}
          <div>
            <div className="kr-overline mb-1.5">NOTES (OPTIONAL)</div>
            <Textarea value={form.notes} onChange={set("notes")} rows={2} className="bg-[#07080B] border-white/10 resize-none" data-testid="app-notes" />
          </div>
          <Button onClick={submit} disabled={busy} className="w-full h-12 rounded-xl font-semibold text-white border-0 sticky bottom-0" style={{ background: "linear-gradient(90deg,#FF3AA8,#8A52FF)" }} data-testid="app-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit application"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Billing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [trialMode, setTrialMode] = useState(true);
  const [applyPlan, setApplyPlan] = useState(null);
  const [txs, setTxs] = useState([]);

  useEffect(() => {
    api.get("/billing/plans").then((r) => { setPlans(r.data.plans); setTrialMode(!!r.data.trial_mode); }).catch(() => {});
    api.get("/credits/me").then((r) => setTxs(r.data.transactions || [])).catch(() => {});
    if (new URLSearchParams(window.location.search).get("whish_failed")) {
      toast.error("Payment was not completed — your plan was not changed.");
    }
  }, []);

  const ctaLabel = (p) => {
    if (p.contact_sales) return <>Contact Sales <Phone className="h-4 w-4 ml-2" /></>;
    if (p.id === "free") return "Free forever";
    if (user?.plan === p.id) return "Current plan";
    if (trialMode) return <>Request Trial <Sparkles className="h-4 w-4 ml-2" /></>;
    return "Upgrade";
  };

  const onCta = (p) => {
    if (p.id === "free") return;
    setApplyPlan(p);
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kr-overline text-[#00E5FF]">[ CREDITS & PLANS ]</div>
          <h1 className="font-display text-4xl mt-2 tracking-tight">Credits</h1>
          <p className="text-white/55 text-sm mt-1.5">One credit powers one AI action. Choose the plan that fits your workflow.</p>
        </div>
        <div className="kr-card px-5 py-3 flex items-center gap-4">
          <div>
            <div className="kr-overline">CURRENT</div>
            <div className="font-display text-lg capitalize" data-testid="current-plan">{user?.plan}</div>
          </div>
          <div className="h-8 w-px bg-white/10" />
          <div className="flex items-center gap-2">
            <Coins className="h-4 w-4 text-[#00E5FF]" />
            <span className="font-display text-lg" data-testid="current-credits">{user?.credits?.toLocaleString()}</span>
            <span className="text-xs text-white/40">credits</span>
          </div>
        </div>
      </div>

      {trialMode && (
        <div className="mt-6 kr-card p-5 border-[#00E5FF]/30 bg-[#00E5FF]/5" data-testid="trial-banner">
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-[#00E5FF] mt-0.5 shrink-0" />
            <div>
              <div className="font-display text-base">AlphaCore is currently in its trial phase.</div>
              <div className="text-sm text-white/60 mt-1">Payments are disabled while we prepare for the official launch. Join the trial list to secure early access and launch pricing.</div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((p) => (
          <div key={p.id} className={`kr-card p-6 relative flex flex-col ${p.popular ? "border-[#00E5FF]/40 kr-shadow-cyan" : ""}`} data-testid={`plan-${p.id}`}>
            {p.popular && <div className="absolute -top-3 left-6 bg-[#00E5FF] text-black text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-sm">MOST POPULAR</div>}
            <div className="font-display text-xl">{p.name}</div>
            <div className="mt-4 flex items-baseline gap-1">
              {p.amount === null || p.amount === undefined ? (
                <span className="font-display text-2xl text-white/70">Contact Us</span>
              ) : (
                <>
                  <span className="font-display text-4xl">${p.amount}</span>
                  <span className="text-white/40 text-sm">/mo</span>
                </>
              )}
            </div>
            {p.credits !== null && p.credits !== undefined && (
              <div className="kr-overline mt-1">{p.credits.toLocaleString()} credits</div>
            )}
            <ul className="mt-6 space-y-2.5 flex-1">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-white/70"><Check className="h-4 w-4 text-[#00E5FF] mt-0.5 shrink-0" /> {f}</li>
              ))}
            </ul>
            <Button
              onClick={() => onCta(p)}
              disabled={user?.plan === p.id || p.id === "free"}
              className={`mt-8 rounded-full ${p.popular ? "bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90" : "bg-white/5 text-white hover:bg-white/10"}`}
              data-testid={`cta-${p.id}-btn`}
            >
              {ctaLabel(p)}
            </Button>
          </div>
        ))}
      </div>

      {/* Credit transactions ledger */}
      <div className="mt-10 kr-card p-6">
        <div className="kr-overline">[ CREDIT LEDGER ]</div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-white/40 border-b border-white/5">
                <th className="text-left p-2 font-mono text-[10px] tracking-wider uppercase">Date</th>
                <th className="text-left p-2 font-mono text-[10px] tracking-wider uppercase">Reason</th>
                <th className="text-right p-2 font-mono text-[10px] tracking-wider uppercase">Δ</th>
                <th className="text-right p-2 font-mono text-[10px] tracking-wider uppercase">Balance</th>
                <th className="text-left p-2 font-mono text-[10px] tracking-wider uppercase">Note</th>
              </tr>
            </thead>
            <tbody>
              {txs.map((t) => (
                <tr key={t.tx_id} className="border-b border-white/5">
                  <td className="p-2 text-xs text-white/50">{new Date(t.created_at).toLocaleString()}</td>
                  <td className="p-2 font-mono text-xs">{t.reason}</td>
                  <td className={`p-2 text-right font-mono ${t.delta >= 0 ? "text-[#34D399]" : "text-[#F472B6]"}`}>{t.delta > 0 ? "+" : ""}{t.delta}</td>
                  <td className="p-2 text-right font-mono text-white/70">{t.new_balance}</td>
                  <td className="p-2 text-xs text-white/50">{t.note || "—"}</td>
                </tr>
              ))}
              {txs.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center text-white/40">No credit transactions yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {applyPlan && <PlanApplicationDialog plan={applyPlan} trialMode={trialMode} onClose={() => setApplyPlan(null)} />}
    </div>
  );
}
